import { DOCUMENT, registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, Inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CldrIntlService, IntlService, setData } from '@progress/kendo-angular-intl';
import { MessageService } from '@progress/kendo-angular-l10n';
import dayjs from 'dayjs';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, from, Observable, of, switchMap, tap } from 'rxjs';
import { LanguageChanged } from '../../root-store/actions';
import { RootState } from '../../root-store/root-state.model';
import { DayjsDateAdapter } from '../material-dayjs-adapter/dayjs-date-adapter';

export interface SupportedCulture {
  culture: string
  language: string
  localName: string
}

@Injectable({
  providedIn: 'root'
})
export class LocaleService {
  public readonly supported: SupportedCulture[] = [
    { culture: 'en-US', language: 'en', localName: 'English' },
    { culture: 'de-CH', language: 'de', localName: 'Deutsch' },
    { culture: 'fr-CH', language: 'fr', localName: 'Français' },
    { culture: 'it-CH', language: 'it', localName: 'Italiano' }
  ]
  public readonly supportedCultures = this.supported.map(s => s.culture) //['en-US', 'de-CH', 'fr-CH', 'it-CH']; // index 0 is default Culture

  public currentLocale: Signal<string> = signal(this.ngxTranslateService.currentLang)

  public currentLang = computed(() => this.currentLocale().substring(0, 2))

  constructor(
    private kendoIntlSvc: IntlService,
    private ngxTranslateService: TranslateService,
    private cookieService: CookieService,
    private store: Store<RootState>,
    private kendoMessageService: MessageService,
    private http: HttpClient,
    private materialDateAdapter: DateAdapter<DayjsDateAdapter>,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  async initTranslationsAsync(): Promise<void> {
    this.ngxTranslateService.addLangs(this.supportedCultures)
    this.ngxTranslateService.setDefaultLang(this.supportedCultures[0])

    // notify Kendo when translations have been loaded from the server
    this.ngxTranslateService.onLangChange.subscribe(langChange => {
      this.kendoMessageService.notify()
      // dispatch language change action, so that page title can be updated and cart is reloaded
      this.store.dispatch(LanguageChanged({ lang: langChange.lang }))
    })

    const useCulture = this.getCultureFromUrl() || this.getCultureFromCookie() || this.getCultureFromBrowser() || this.supportedCultures[0]
    this.ngxTranslateService.use(useCulture)
    await this.setCultureInternalAsync(useCulture)
  }

  private getCultureFromUrl() {
    const pathParts = location.pathname.split('/')
    if (pathParts.length < 3)
      return;

    return this.tryGetSupportedCulture([pathParts[2]])
  }

  // get the first culture that is supported by our app
  private getCultureFromBrowser() {
    return this.tryGetSupportedCulture(window.navigator.languages)
  }

  private tryGetSupportedCulture(languages: readonly string[]) {
    const supportedLanguages = this.supported.map(s => s.language)

    for (const lang of languages.map(lang => lang.substring(0, 2).toLowerCase())) {
      if (supportedLanguages.includes(lang))
        return this.supportedCultures.find(l => l.substring(0, 2) === lang)!
    }

    return undefined
  }

  public getCultureFromCookie(): string {
    const culture = this.cookieService.get('Culture')

    // 2024-10-15 replace 'en' with 'en-US' because we changed the default language from en to en-US
    if (culture === 'en')
      return 'en-US'

    return culture
  }

  async setCultureAsync(culture: string): Promise<void> {
    await this.setCultureInternalAsync(culture, true)
  }

  private async setCultureInternalAsync(culture: string, setNgxLocale: boolean = false): Promise<void> {
    // set the culture-cookie to persist the language and make it available to the server
    this.cookieService.set('Culture', culture, 360, '/', undefined, true, 'None');

    (this.currentLocale as WritableSignal<string>).set(culture)

    this.document.documentElement.lang = culture

    // define all locales that need to be loaded, so that we can load them in parallel
    let angularLocales: Promise<any>
    let dayjsLocale: Promise<ILocale | null>
    switch (culture.toLocaleLowerCase()) {
      case 'de-ch':
        angularLocales = import('@angular/common/locales/de-CH')
        dayjsLocale = import('dayjs/locale/de-ch')
        break
      case 'fr-ch':
        angularLocales = import('@angular/common/locales/fr-CH')
        dayjsLocale = import('dayjs/locale/fr-ch')
        break
      case 'it-ch':
        angularLocales = import('@angular/common/locales/it-CH')
        dayjsLocale = import('dayjs/locale/it-ch')
        break
      default:
        angularLocales = Promise.resolve(null)
        dayjsLocale = Promise.resolve(null) // By default, Day.js comes with English locale, so no need to import it
    }

    // load angular locales before ngx-translate, so that ngx-translate can use them (e.g. for date pipe)
    const angularLocalesAndNgxTranslate = angularLocales.then(angularLocales => {
      if (angularLocales)
        registerLocaleData(angularLocales.default, culture)

      if (setNgxLocale)
        this.ngxTranslateService.use(culture)
    })

    const dayjsLoader = dayjsLocale.then(() => {
      dayjs.locale(culture)
      this.materialDateAdapter.setLocale(culture)
    })

    const kendoLoader$ = this.loadKendoTranslations(culture)
      .pipe(tap(() => {
        (this.kendoIntlSvc as CldrIntlService).localeId = culture // Kendo Scheduler
      }))

    combineLatest([
      from(angularLocalesAndNgxTranslate),
      from(dayjsLoader),
      kendoLoader$
    ]).subscribe()
  }

  // #region Kendo

  private loadedKendoLocales: { [key: string]: boolean } = {
    'en-US': true
  }
  private kendoLocalesBaseUrl = "assets/kendo-angular-intl"

  private loadKendoTranslations(localeId: string): Observable<boolean> {
    if (this.loadedKendoLocales[localeId])
      return of(true)

    return combineLatest([
      this.http.get(`${this.kendoLocalesBaseUrl}/${localeId}/numbers.json`),
      this.http.get(`${this.kendoLocalesBaseUrl}/${localeId}/calendar.json`)
    ]).pipe(switchMap(([numbers, calendar]) => {
      setData(numbers)
      setData(calendar)
      this.loadedKendoLocales[localeId] = true

      return of(true)
    }))
  }

  // #endregion
}
